"use client";

// Pelacak pengunjung storefront (metrik #14 dashboard admin). Sekali per
// pengunjung per hari (WIB dedup di server via on-conflict), fire-and-forget.
// HANYA storefront — dilewati di /admin. Tak merender UI.

import { useEffect } from "react";
import { createBrowserSupabase } from "@/lib/supabase/browser";
import { isPlatformHost } from "@/lib/platformHost";
import {
  STORE_FIRST_TOUCH_KEY,
  buildStoreAttribution,
  firstTouchStillValid,
  hasAnyAttribution,
  hasUtmAttribution,
  parseFirstTouch,
  shouldRecordStoreVisit,
} from "@/lib/storeAttribution";

const VISITOR_ID_KEY = "galea-visitor-id";
const VISITED_DAY_KEY = "galea-visited-day";

/** Tanggal lokal "YYYY-MM-DD" (zona browser = WIB untuk pengguna toko). */
function localDay(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getVisitorId(): string {
  let id = localStorage.getItem(VISITOR_ID_KEY);
  if (!id) {
    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(VISITOR_ID_KEY, id);
  }
  return id;
}

export function VisitorTracker({ storeId = null }: { storeId?: string | null }) {
  useEffect(() => {
    // Hanya hitung kunjungan storefront (bukan panel admin).
    // /platform = halaman jualan platform di apex kelolify.com — pengunjungnya
    // calon penyewa, bukan pembeli toko; mencatatnya akan mengotori metrik
    // "Pengunjung Website" di dashboard admin toko (dan menulis ke DB dari
    // halaman yang seharusnya tak menyentuh DB sama sekali).
    // Pagar HOST harus lebih dulu. Di apex platform, `/` di-REWRITE ke
    // `/platform` oleh middleware, jadi window.location.pathname TETAP "/" —
    // cek path di bawah TIDAK PERNAH menyala di sana. Tanpa pagar ini halaman
    // jualan SaaS ikut memanggil record_page_view dan mengotori metrik
    // "Pengunjung Website" milik toko.
    if (isPlatformHost(window.location.hostname)) return;

    // Pertahanan berlapis: /platform juga bisa dibuka lewat domain toko.
    const path = window.location.pathname;
    if (path.startsWith("/admin") || path.startsWith("/platform")) return;

    try {
      const today = localDay();

      // Atribusi kunjungan (mig 4170): UTM dari URL + host perujuk eksternal.
      const attribution = buildStoreAttribution({
        search: window.location.search,
        referrer: document.referrer,
        currentHost: window.location.hostname,
      });
      const hasUtm = hasUtmAttribution(attribution);

      // FIRST-TOUCH untuk atribusi ORDER: simpan sekali (30 hari) di
      // localStorage — per-origin, otomatis ter-scope per toko. Dibaca lagi
      // saat checkout sukses (recordOrderAttribution).
      if (hasAnyAttribution(attribution)) {
        const existing = parseFirstTouch(
          localStorage.getItem(STORE_FIRST_TOUCH_KEY)
        );
        if (!firstTouchStillValid(existing, Date.now())) {
          localStorage.setItem(
            STORE_FIRST_TOUCH_KEY,
            JSON.stringify({ ...attribution, at: new Date().toISOString() })
          );
        }
      }

      // Kirim bila hari ini belum tercatat ATAU URL membawa UTM — klik iklan
      // selalu dilaporkan; server mengisi atribusi yang masih kosong
      // (upsert isi-kalau-kosong, mig 4170 cermin 4100).
      if (
        !shouldRecordStoreVisit({
          today,
          lastVisitedDay: localStorage.getItem(VISITED_DAY_KEY),
          hasUtm,
        })
      ) {
        return;
      }

      const visitorId = getVisitorId();
      const db = createBrowserSupabase("store");
      if (!db) return;

      // Fire-and-forget: kegagalan tracking tak boleh mengganggu storefront.
      // Query-builder Supabase = thenable (bukan Promise), pakai onFulfilled +
      // onRejected, bukan .catch.
      // MULTI-TENANT FASE 4 (mig 3130): stamp toko domain ini — pengunjung
      // teratribusi ke toko yang benar di dashboard per-toko.
      db.rpc("record_page_view", {
        p_visitor_id: visitorId,
        p_store_id: storeId,
        p_referrer: attribution.referrerHost,
        p_utm_source: attribution.utmSource,
        p_utm_medium: attribution.utmMedium,
        p_utm_campaign: attribution.utmCampaign,
      }).then(
        ({ error }) => {
          if (!error) {
            localStorage.setItem(VISITED_DAY_KEY, today);
            return;
          }
          // Fallback darurat: mig 4170 belum ter-apply → signature 6-arg tak
          // dikenal PostgREST. Kunjungan tetap dicatat lewat signature lama
          // supaya metrik "Pengunjung Website" tidak bolong selama jendela
          // deploy (urutan yang benar tetap: migrasi mendahului merge).
          db.rpc("record_page_view", {
            p_visitor_id: visitorId,
            p_store_id: storeId,
          }).then(
            ({ error: legacyError }) => {
              if (!legacyError) localStorage.setItem(VISITED_DAY_KEY, today);
            },
            () => {
              /* abaikan error jaringan */
            }
          );
        },
        () => {
          /* abaikan error jaringan */
        }
      );
    } catch {
      /* localStorage tak tersedia (private mode) → abaikan */
    }
  }, [storeId]);

  return null;
}
